import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
} from '@angular/core';
import { BdGraphApiUser } from '@bindoc-projects/bindoc-permissions-lib/data';
import { BdSharePermissionInfo } from '../../../permissions/share-permission-info.model';
import {
  BdShareDialogTypeComponent,
  ShareDialogGranteeType,
} from '../../../permissions/share-dialog-type/share-dialog-type.component';
import { BdUserSearchComponent } from '../../../select/user-search/user-search.component';
import { BdCombinedGroupSelectComponent } from '../../../select/combined-group/combined-group-select.component';
import { BdUiSingleSelectComponent, BdUiSingleSelectConfig } from '@bindoc-projects/ui-lib/selection/ui';
import {
  BdCompanyGroup,
  BdSharePermission,
} from '@bindoc-projects/resource-domain-lib';
import { of } from 'rxjs';

import { GrantConfig } from '../../dialog.models';

@Component({
  selector: 'bd-grant-form',
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div class="flex flex-col gap-3">
      <bd-share-dialog-type [(type)]="granteeType"></bd-share-dialog-type>
      @if (granteeType() === 'USER') {
        <bd-user-search (userSelect)="onUserSelect($event)"></bd-user-search>
      } @else {
        <bd-combined-group-select
          (groupSelect)="onGroupSelect($event)"
        ></bd-combined-group-select>
      }
      <bd-ui-single-select
        [config]="permissionConfig"
        [selectOptions]="permissionOptions$"
        [initialSelection]="initialPermissionInfo()"
        (selection)="onPermissionSelect($event)"
      ></bd-ui-single-select>
    </div>
  `,
  imports: [
    BdShareDialogTypeComponent,
    BdUserSearchComponent,
    BdCombinedGroupSelectComponent,
    BdUiSingleSelectComponent,
  ],
})
export class BdGrantFormComponent {
  public readonly itemCount = input(0);
  /**
   * Pre-selects the permission level in the dropdown. Used when re-granting
   * an existing grant so the user sees the permission they already hold.
   */
  public readonly initialPermission = input<BdSharePermission | undefined>(
    undefined,
  );
  public readonly configChange = output<GrantConfig | null>();

  public readonly granteeType = signal<ShareDialogGranteeType>('GROUP');
  public readonly permissionOptions$ = of(BdSharePermissionInfo.tree('DELETE'));
  public readonly initialPermissionInfo = computed<
    BdSharePermissionInfo | undefined
  >(() => {
    const permission = this.initialPermission();
    return permission ? BdSharePermissionInfo.of(permission) : undefined;
  });
  public readonly permissionConfig: BdUiSingleSelectConfig<BdSharePermissionInfo> =
    {
      selectLabel: 'Berechtigung',
      option: {
        uniqueFnc: (i) => i.permission,
        sortFnc: (a, b) => a.weight - b.weight,
        searchOptionAccessor: (i) => i.explanation,
      },
      // Re-grant flow pre-selects a permission via `initialSelection`; emit so
      // the parent receives the pre-filled value without an extra user click.
      emitEventOnInitialSelection: true,
    };

  private _selectedUserId: string | undefined;
  private _selectedGroupId: string | undefined;
  private _selectedPermission: BdSharePermissionInfo | undefined;

  public onUserSelect(user: BdGraphApiUser): void {
    this._selectedUserId = user.id;
    this._emit();
  }

  public onGroupSelect(group: BdCompanyGroup): void {
    this._selectedGroupId = group.id;
    this._emit();
  }

  public onPermissionSelect(info: BdSharePermissionInfo | undefined): void {
    this._selectedPermission = info;
    this._emit();
  }

  private _emit(): void {
    const type = this.granteeType();
    const granteeId =
      type === 'USER' ? this._selectedUserId : this._selectedGroupId;
    if (!granteeId || !this._selectedPermission) {
      this.configChange.emit(null);
      return;
    }
    this.configChange.emit({
      granteeType: type === 'USER' ? 'user' : 'group',
      granteeId,
      permission: this._selectedPermission.permission,
    });
  }
}
